import "./App.css";
import React, { useState, useEffect, useRef } from "react";
import Login from "./Pages/Login/Login";
import Landing from "./Pages/Landing/Landing";
import "./Components/CSS/Common.css";

import { useNavigate, useLocation } from "react-router-dom";

import { FULL_API_BASE_URL } from "./Config/Config";

// reduxtoolkit
// import { fetchApiData } from "./Redux/features/Landing/landingSlice";
// import { useSelector, useDispatch } from "react-redux";

function App() {
  const [authenticated, setAuthenticated] = useState(false);
  const [isLoggedOut, setIsLoggedOut] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const isLoggedIn =
      localStorage.getItem("isLoggedIn") === "true" ||
      sessionStorage.getItem("isLoggedIn") === "true";
    setAuthenticated(isLoggedIn);
  }, []);

  useEffect(() => {
    const currentPath = location.pathname;
    if (authenticated && isLoggedOut) {
      navigate("/"); // Navigates to the Home page
    }
  }, [authenticated]);

  useEffect(() => {
    const path = location.pathname;
    const isOnQuestionPaperPage = path === "/question_paper_details";

    if (!isOnQuestionPaperPage) {
      const keysToRemove = [
        "subjectWiseSelectedRowIds",
        "subjectWiseDeletedRowIds",
        "subjectWiseSectionMap",
        "subjectWiseQuesTypeIds",
      ];

      keysToRemove.forEach((key) => localStorage.removeItem(key));
    }
  }, [location]);

  const handleLogout = async () => {
    try {
      const userId = localStorage.getItem("userId");
      const clientId = localStorage.getItem("clientId");

      if (userId && clientId) {
        const url = `${FULL_API_BASE_URL}/logout-web?user_id=${encodeURIComponent(
          userId
        )}&client_id=${encodeURIComponent(clientId)}`;

        const res = await fetch(url, {
          method: "PUT",
          headers: {
            accept: "application/json",
          },
        });

        // Even if non-200, proceed with cleanup
        try {
          const data = await res.json();
          // Optionally, check data.status === 200
          // console.log("Logout API:", data);
        } catch (_) {
          // Ignore JSON parse issues
        }
      }
    } catch (e) {
      // Ignore network errors and proceed to cleanup
    } finally {
      // Clear all relevant localStorage items
      localStorage.removeItem("isLoggedIn");
      localStorage.removeItem("userId");
      localStorage.removeItem("clientId");
      localStorage.removeItem("clientBranchId");
      localStorage.removeItem("pagePath");
      localStorage.removeItem("auth");

      // OAuth-related keys if present
      localStorage.removeItem("other");
      localStorage.removeItem("other_type");
      localStorage.removeItem("other_expires_in");

      // Any additional app-specific caches
      localStorage.removeItem("ipInfo");

      // Update UI state
      setAuthenticated(false);
      setIsLoggedOut(true);
      // Optional: reset page path preference
      localStorage.setItem("pagePath", "Home");
      // setOpen(false);
      navigate("/");
    }
  };

  useEffect(() => {
    // Remove the unused currentPath variable and fix condition
    if (!authenticated && isLoggedOut) {
      navigate("/"); // ✅ Navigate to root on logout
    }
  }, [authenticated, isLoggedOut]); // ✅ Add isLoggedOut as dependency

  // const useInactivityLogout = (logoutCallback, timeoutMinutes = 0.15, isEnabled = true) => {
  const useInactivityLogout = (
    logoutCallback,
    timeoutMinutes = 20,
    isEnabled = true
  ) => {
    const timeoutRef = useRef(null);
    const intervalRef = useRef(null);
    const [timeLeft, setTimeLeft] = useState(Math.ceil(timeoutMinutes * 60));
    const endTimeRef = useRef(Date.now() + timeoutMinutes * 60 * 1000);
    const lastActivityRef = useRef(0);
    const hasLoggedOutRef = useRef(false);

    const clearTimers = () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (intervalRef.current) clearInterval(intervalRef.current);
      timeoutRef.current = null;
      intervalRef.current = null;
    };

    const startCountdown = () => {
      // Ensure we clear any existing interval
      if (intervalRef.current) clearInterval(intervalRef.current);

      intervalRef.current = setInterval(() => {
        const remaining = Math.max(
          0,
          Math.ceil((endTimeRef.current - Date.now()) / 1000)
        );
        setTimeLeft(remaining);

        // Log the countdown timer
        // console.log(`⏱️ Inactivity Timer: ${remaining} seconds remaining`);

        if (remaining === 0 && !hasLoggedOutRef.current) {
          // console.log("🚪 User logged out due to inactivity at", new Date().toLocaleString());
          hasLoggedOutRef.current = true;
          if (typeof logoutCallback === "function") logoutCallback();
          clearTimers();
        }
      }, 1000);
    };

    const resetTimer = (activityType = "unknown") => {
      const now = Date.now();

      // Debounce log to avoid flood (e.g., many mousemove or key repeats)
      if (now - lastActivityRef.current > 500) {
        // console.log(`Activity detected: ${activityType} at ${new Date().toLocaleTimeString()}`);
        lastActivityRef.current = now;
      }

      clearTimers();
      hasLoggedOutRef.current = false;

      // compute new end time and update immediate display
      endTimeRef.current = Date.now() + timeoutMinutes * 60 * 1000;
      setTimeLeft(Math.ceil((endTimeRef.current - Date.now()) / 1000));

      // Start the timeout as a backup (not strictly required because interval handles logout)
      if (typeof logoutCallback === "function") {
        timeoutRef.current = setTimeout(() => {
          // safety: in case interval missed it
          if (!hasLoggedOutRef.current) {
            // console.log("User logged out due to inactivity (timeout) at", new Date().toLocaleString());
            hasLoggedOutRef.current = true;
            logoutCallback();
          }
          clearTimers();
        }, timeoutMinutes * 60 * 1000);
      }

      // Start interval countdown
      startCountdown();
    };

    useEffect(() => {
      // Only set up listeners if enabled (authenticated)
      if (!isEnabled) {
        clearTimers();
        return;
      }

      // include keyboard and input events
      const events = [
        "mousedown",
        "mousemove",
        "click",
        "scroll",
        "touchstart",
        "keydown", // <-- catches key presses
        "keypress", // optional, included for compatibility
      ];

      const handleActivity = (e) => resetTimer(e.type);

      events.forEach((ev) => window.addEventListener(ev, handleActivity));
      // also listen for 'input' on document to catch typing inside inputs / IME compositions
      const handleInput = (e) => resetTimer("input");
      document.addEventListener("input", handleInput);

      // Initialize timer only once when component mounts or isEnabled changes
      console.log("🟢 Inactivity monitoring started");
      resetTimer("session start");

      return () => {
        console.log("🔴 Inactivity monitoring stopped");
        clearTimers();
        events.forEach((ev) => window.removeEventListener(ev, handleActivity));
        document.removeEventListener("input", handleInput);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEnabled]);

    return timeLeft;
  };

  // Always call the hook, but only enable it when authenticated
  const timeLeft = useInactivityLogout(handleLogout, 20, authenticated);

  // const { data, loading, error } = useSelector((state) => state.landing);
  // const dispatch = useDispatch();

  // useEffect(() => {
  //   dispatch(fetchApiData());
  // }, [dispatch]);

  // useEffect(() => {
  //   if (data !== null) {
  //     // console.log("Landing Data", data);
  //   }
  // }, [data]);

  // if (loading) {
  //   return <div>Loading...</div>;
  // }

  // if (error) {
  //   return <div>Error: {error}</div>;
  // }

  return (
    <div>
      {/* <h1>React</h1> */}

      {authenticated ? (
        <Landing
          setAuthenticated={setAuthenticated}
          setIsLoggedOut={setIsLoggedOut}
        />
      ) : (
        <Login setAuthenticated={setAuthenticated} />
      )}

      {/* <h1>{JSON.stringify(data, null, 2)}</h1> */}
    </div>
  );
}

export default App;
